import { Container } from '@/components/Container';

/* ═══════════════════════════════════════════
   MELHOR ÉPOCA — Calendário de temporadas
   Clima, movimento e preço por mês
   ═══════════════════════════════════════════ */

interface MonthData {
  month: string;
  abbr: string;
  season: 'alta' | 'media' | 'baixa';
  climaIcon: string;
  climaLabel: string;
  movimento: 'alto' | 'medio' | 'baixo';
  precoLabel: string;
  destaque?: string;
}

const mesesData: MonthData[] = [
  {
    month: 'Janeiro',
    abbr: 'Jan',
    season: 'alta',
    climaIcon: '☀️',
    climaLabel: 'Ótimo',
    movimento: 'alto',
    precoLabel: '+40–70%',
    destaque: 'Verão pleno',
  },
  {
    month: 'Fevereiro',
    abbr: 'Fev',
    season: 'alta',
    climaIcon: '☀️',
    climaLabel: 'Ótimo',
    movimento: 'alto',
    precoLabel: '+40–70%',
  },
  {
    month: 'Março',
    abbr: 'Mar',
    season: 'alta',
    climaIcon: '🌤️',
    climaLabel: 'Bom',
    movimento: 'alto',
    precoLabel: '+40–70%',
  },
  {
    month: 'Abril',
    abbr: 'Abr',
    season: 'media',
    climaIcon: '🌤️',
    climaLabel: 'Bom',
    movimento: 'medio',
    precoLabel: 'Normal',
  },
  {
    month: 'Maio',
    abbr: 'Mai',
    season: 'baixa',
    climaIcon: '🌦️',
    climaLabel: 'Variável',
    movimento: 'baixo',
    precoLabel: '–10–20%',
  },
  {
    month: 'Junho',
    abbr: 'Jun',
    season: 'baixa',
    climaIcon: '🌧️',
    climaLabel: 'Fresco',
    movimento: 'baixo',
    precoLabel: '–10–20%',
    destaque: 'Fest. da Tainha',
  },
  {
    month: 'Julho',
    abbr: 'Jul',
    season: 'alta',
    climaIcon: '🌤️',
    climaLabel: 'Bom',
    movimento: 'alto',
    precoLabel: '+40–70%',
    destaque: 'Férias de inverno',
  },
  {
    month: 'Agosto',
    abbr: 'Ago',
    season: 'baixa',
    climaIcon: '🌧️',
    climaLabel: 'Fresco',
    movimento: 'baixo',
    precoLabel: '–10–20%',
  },
  {
    month: 'Setembro',
    abbr: 'Set',
    season: 'baixa',
    climaIcon: '🌦️',
    climaLabel: 'Variável',
    movimento: 'baixo',
    precoLabel: '–10–20%',
  },
  {
    month: 'Outubro',
    abbr: 'Out',
    season: 'media',
    climaIcon: '🌤️',
    climaLabel: 'Bom',
    movimento: 'medio',
    precoLabel: 'Normal',
  },
  {
    month: 'Novembro',
    abbr: 'Nov',
    season: 'media',
    climaIcon: '🌦️',
    climaLabel: 'Variável',
    movimento: 'medio',
    precoLabel: 'Normal',
  },
  {
    month: 'Dezembro',
    abbr: 'Dez',
    season: 'alta',
    climaIcon: '☀️',
    climaLabel: 'Ótimo',
    movimento: 'alto',
    precoLabel: '+40–70%',
    destaque: 'Natal e Réveillon',
  },
];

const seasonBadge: Record<MonthData['season'], string> = {
  alta: 'bg-coral/15 text-coral',
  media: 'bg-amber-100 text-amber-700',
  baixa: 'bg-teal-50 text-teal-deep',
};

const seasonLabel: Record<MonthData['season'], string> = {
  alta: 'Alta',
  media: 'Média',
  baixa: 'Baixa',
};

const movimentoDots: Record<MonthData['movimento'], number> = {
  alto: 3,
  medio: 2,
  baixo: 1,
};

interface ResumoCard {
  season: MonthData['season'];
  title: string;
  desc: string;
}

const resumoCards: ResumoCard[] = [
  {
    season: 'alta',
    title: 'Alta temporada',
    desc: 'Mar quente, ilha cheia, preços elevados — ideal para quem quer praia animada.',
  },
  {
    season: 'media',
    title: 'Média temporada',
    desc: 'Equilíbrio entre movimento e preço — melhor custo-benefício.',
  },
  {
    season: 'baixa',
    title: 'Baixa temporada',
    desc: 'Ilha tranquila, preços baixos, experiências únicas (Festival da Tainha) — para quem quer paz.',
  },
];

export function MelhorEpocaSection() {
  return (
    <section id="melhor-epoca" className="py-16 sm:py-20 bg-sand-50">
      <Container>
        {/* Header */}
        <div className="text-center mb-12">
          <span className="font-body text-sm text-coral font-semibold uppercase tracking-wider">
            Planejamento
          </span>
          <h2 className="font-display text-4xl sm:text-5xl text-warm-dark mt-2">
            Melhor época para visitar
          </h2>
          <p className="font-editorial text-lg text-warm-gray mt-2 max-w-2xl mx-auto">
            Saiba o que muda em cada mês: clima, movimento, preço e experiências únicas.
          </p>
        </div>

        {/* Grid de meses */}
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 mb-12">
          {mesesData.map(m => (
            <div
              key={m.month}
              className="bg-white rounded-xl border border-sand-200 p-3 flex flex-col gap-2 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
            >
              {/* Header: mês abreviado + ícone */}
              <div className="flex items-center justify-between">
                <span className="font-display text-sm text-warm-dark font-semibold">{m.abbr}</span>
                <span role="img" aria-label={m.climaLabel} className="text-base">
                  {m.climaIcon}
                </span>
              </div>

              {/* Badge de temporada */}
              <span
                className={`text-xs font-body font-semibold px-1.5 py-0.5 rounded-full self-start ${seasonBadge[m.season]}`}
              >
                {seasonLabel[m.season]}
              </span>

              {/* Indicador de movimento */}
              <div className="flex gap-1 items-center">
                {[1, 2, 3].map(dot => (
                  <span
                    key={dot}
                    className={`w-2 h-2 rounded-full ${dot <= movimentoDots[m.movimento] ? 'bg-coral' : 'bg-sand-200'}`}
                  />
                ))}
              </div>

              {/* Preço relativo */}
              <span className="font-body text-xs text-warm-gray">{m.precoLabel}</span>

              {/* Destaque */}
              {m.destaque && (
                <span className="bg-teal-deep/10 text-teal-deep text-xs font-body font-semibold px-1.5 py-0.5 rounded-full leading-tight">
                  {m.destaque}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Cards de resumo */}
        <div className="grid md:grid-cols-3 gap-5">
          {resumoCards.map(r => (
            <div
              key={r.season}
              className="bg-white rounded-xl border border-sand-200 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
            >
              <span
                className={`text-xs font-body font-bold px-2 py-0.5 rounded-full inline-block mb-3 ${seasonBadge[r.season]}`}
              >
                {seasonLabel[r.season]}
              </span>
              <h3 className="font-display text-lg text-warm-dark mb-2">{r.title}</h3>
              <p className="font-body text-sm text-warm-gray">{r.desc}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}
